import React from "react";
import {
  CheckCircle2,
  FileWarning,
  IdCard,
  RotateCcw,
  ScanFace,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { KycStep } from "../components/KycStep";
import { KYC_ATTEMPT_STAGES } from "../flow/kycAttemptState";

const STAGE_CONTENT = {
  [KYC_ATTEMPT_STAGES.DOCUMENT]: {
    icon: IdCard,
    title: "kyc.documentVerificationFailed.title",
    description: "kyc.documentVerificationFailed.description",
    button: "kyc.camera.retake",
    tips: [],
  },
  [KYC_ATTEMPT_STAGES.FACE]: {
    icon: ScanFace,
    title: "kyc.faceVerificationFailed.title",
    description: "kyc.faceVerificationFailed.description",
    button: "kyc.faceVerificationFailed.button",
    tips: [
      "kyc.faceVerificationFailed.tips.centerFace",
      "kyc.faceVerificationFailed.tips.moveBack",
      "kyc.faceVerificationFailed.tips.nodHead",
      "kyc.faceVerificationFailed.tips.turnHead",
    ],
  },
  [KYC_ATTEMPT_STAGES.ADDRESS_PROOF]: {
    icon: FileWarning,
    title: "kyc.proofVerificationFailed.title",
    description: "kyc.proofVerificationFailed.description",
    button: "kyc.proofVerificationFailed.button",
    tipsTitle: "kyc.proofVerificationFailed.requirementsTitle",
    tips: [
      "kyc.proofVerificationFailed.requirements.noScreenshots",
      "kyc.proofVerificationFailed.requirements.showEdges",
      "kyc.proofVerificationFailed.requirements.last180Days",
      "kyc.proofVerificationFailed.requirements.readable",
      "kyc.proofVerificationFailed.requirements.pdf",
    ],
  },
};

export function FirstAttemptFailedStep({ stage, onRetry }) {
  const { t } = useTranslation();
  const content =
    STAGE_CONTENT[stage] || STAGE_CONTENT[KYC_ATTEMPT_STAGES.DOCUMENT];
  const Icon = content.icon;

  return (
    <KycStep
      labelledBy="kyc-first-attempt-failed-title"
      className="kyc-status-step first-attempt-failed-step"
    >
      <div className="flex min-h-[42dvh] flex-col items-center justify-center py-4 text-center">
        <span className="flex size-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 ring-1 ring-amber-100">
          <Icon className="size-7" aria-hidden="true" />
        </span>
        <h1
          id="kyc-first-attempt-failed-title"
          className="mt-6 text-[1.35rem] font-semibold leading-tight tracking-[-0.035em] text-slate-950"
        >
          {t(content.title)}
        </h1>
        <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
          {t(content.description)}
        </p>

        {content.tips.length > 0 && (
          <div className="mt-6 w-full max-w-md rounded-xl border border-slate-200 bg-slate-50 p-4 text-left">
            {content.tipsTitle && (
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                {t(content.tipsTitle)}
              </p>
            )}
            <ul className="mt-3 space-y-2 text-xs leading-5 text-slate-600">
              {content.tips.map((tip) => (
                <li key={tip} className="flex gap-2">
                  <CheckCircle2
                    className="mt-0.5 size-3.5 shrink-0 text-teal-700"
                    aria-hidden="true"
                  />
                  <span>{t(tip)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          type="button"
          onClick={onRetry}
          className="mt-7 inline-flex min-h-12 w-full max-w-md items-center justify-center gap-2 rounded-lg bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-800 active:scale-[0.99]"
        >
          <RotateCcw className="size-4" aria-hidden="true" />
          {t(content.button)}
        </button>
      </div>
    </KycStep>
  );
}
